import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { DomainKeyValue, KeyValueTypes } from '@vendasta/task';
import {
  SearchTaskRequest,
  SearchTaskRequestInterface,
  TaskInterface,
  TaskManagerApiService,
  TemplateInterface,
  TemplatesSdkService,
} from '@vendasta/task-manager';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, map, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { AppsService } from '../../../../../data-services/apps.service';

@Component({
  selector: 'automata-project-template-selector',
  templateUrl: './project-template-selector.component.html',
  styleUrls: ['./project-template-selector.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    MatAutocompleteModule,
  ],
})
export class ProjectTemplateSelectorComponent implements OnInit {
  @Input() form: UntypedFormGroup;
  @Input() templateId: string;
  @Input() title: string;
  @Output() savingEnabled: EventEmitter<boolean> = new EventEmitter();
  loading = true;

  disablePartnerSpecificTemplateSelection$: Observable<boolean>;

  systemTemplates$: Observable<TemplateInterface[]>;
  yourTemplates$: Observable<TaskInterface[]>;

  constructor(
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) private readonly partnerId$: Observable<string>,
    protected readonly automationService: AutomationViewService,
    private templatesSdkService: TemplatesSdkService,
    private taskManagerApiService: TaskManagerApiService,
    private appsService: AppsService,
  ) {}

  ngOnInit(): void {
    this.form.addControl('template', new UntypedFormControl('', this.templateValidator));

    const templateControlValueChanges$ = this.form.controls?.template?.valueChanges.pipe(startWith(''));

    const systemTemplatesFetch$ = this.templatesSdkService
      .listTemplates('partner/global', 250, '')
      .pipe(map((resp) => resp.templates || []));

    this.systemTemplates$ = combineLatest(systemTemplatesFetch$, templateControlValueChanges$).pipe(
      map(([templates, value]) => {
        let title = '';
        if (typeof value === 'string') {
          title = value;
        }
        // TODO support title search on listTemplates instead
        return templates.filter((t) => t.title.toLowerCase().includes(title.toLowerCase()));
      }),
    );

    this.disablePartnerSpecificTemplateSelection$ = this.automationService.isNonPartnerAutomation().pipe(
      take(1),
      tap((isNonPartnerAutomation) => {
        if (isNonPartnerAutomation) {
          return true;
        } else {
          this.form.controls?.template.setValidators([Validators.required, this.templateValidator]);
          return false;
        }
      }),
    );

    this.yourTemplates$ = templateControlValueChanges$.pipe(
      debounceTime(200),
      withLatestFrom(this.partnerId$, this.disablePartnerSpecificTemplateSelection$),
      switchMap(([value, partnerId, disablePartnerSpecificTemplate]) => {
        if (disablePartnerSpecificTemplate) {
          return of({ tasks: [] });
        }
        let title = '';
        if (typeof value === 'string') {
          title = value;
        }

        const req: SearchTaskRequestInterface = new SearchTaskRequest({
          namespace: `partner/${partnerId}`,
          titleFuzzy: title,
          types: ['Template', 'VendorTemplate'],
          cursor: '0',
          pageSize: 250,
          sorts: [
            {
              field: 'title',
              direction: 1,
            },
          ],
        });
        return this.taskManagerApiService.search(req);
      }),
      map((req) => req.tasks),
    );

    combineLatest([this.yourTemplates$, this.systemTemplates$])
      .pipe(
        take(1),
        tap(([yourTemplates, systemTemplates]) => {
          if (this.templateId) {
            let selected = yourTemplates.find((t) => t.identity.taskId === this.templateId);
            if (!selected) {
              selected = systemTemplates.find((t) => t.identity.templateId === this.templateId);
            }
            if (selected) {
              this.form.controls.template.setValue(selected);
            }
          }
        }),
      )
      .subscribe(() => {
        this.loading = false;
        this.savingEnabled.emit(true);
      });
  }

  templateTitle(template: TemplateInterface | TaskInterface): string {
    return template?.title;
  }

  templateProductName(template: TemplateInterface | TaskInterface): Observable<string> {
    if (!template || !('metadata' in template)) {
      return of('');
    }
    const metadata = DomainKeyValue.fromKeyValue((template as TaskInterface).metadata);
    const productId = metadata.get('ProductId', KeyValueTypes.STRING)[0] as string;
    if (!productId) {
      return of('');
    }
    return this.appsService.getApps().pipe(
      map((apps) => {
        const product = apps.find((app) => app.key.appId === productId);
        return product?.sharedMarketingInformation?.name || '';
      }),
    );
  }

  templateValidator(control: AbstractControl): { [key: string]: any } {
    const invalid = !!control.value && typeof control.value === 'string';
    return invalid ? { template: 'invalid' } : null;
  }
}
